import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { DealStatusService } from '@zaoblako/web/core/services/deal/deal-status.service';
import type { DealStatusFindInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import type { DealStatusFindManyQueryInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find-many-query.dto';

import { CommonModule } from '@angular/common';
import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  type OnInit
} from '@angular/core';

@Component({
  standalone: true,
  selector: 'crm-deal-status-single-select',
  templateUrl: './deal-status-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SingleSelectComponent,
    SpinnerComponent,
    CommonModule
  ]
})
export class DealStatusSingleSelect implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public statuses: DealStatusFindInterface[] = [];
  public queryParams: DealStatusFindManyQueryInterface = {
    limit: 40,
    offset: 0,
    select: ['id', 'name']
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedStatus: DealStatusFindInterface;
  @Input() public set selectedStatus(selectedStatus: DealStatusFindInterface) {
    if (this.customSearch) {
      this._selectedStatus = selectedStatus;
      return;
    }

    if (this.statuses.some((status) => status.id === selectedStatus?.id)) {
      this._selectedStatus = this.statuses.find(
        (status) => status.id === selectedStatus.id
      );
      return;
    }

    if (selectedStatus?.id) {
      this.dealStatusService.findById<DealStatusFindInterface>(selectedStatus.id).subscribe({
        next: (_status) => {
          if (!this.statuses.some((status) => status.id === _status.id)) {
            this.statuses = this.statuses.concat(_status);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedStatus = _status;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedStatusChange = new EventEmitter<DealStatusFindInterface>();

  @Input() public customSearch = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public appendTo: string;
  @Input() public labelInput = false;

  @Input() public set setSelectedStatusId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    if (this.statuses.some((status) => status.id === id)) {
      this._selectedStatus = this.statuses.find((status) => status.id === id);
      return;
    }

    this.dealStatusService.findById<DealStatusFindInterface>(id).subscribe({
      next: (_status) => {
        if (!this.statuses.some((status) => status.id === _status.id)) {
          this.statuses = this.statuses.concat(_status);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedStatus = _status;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly dealStatusService: DealStatusService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.dealStatusService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.statuses = this.statuses?.length
            ? this.statuses.concat(
                data.items.filter((item) => !this.statuses.some((status) => status.id === item.id))
              )
            : data.items.filter((item) => !this.statuses.some((status) => status.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(status: DealStatusFindInterface) {
    this.selectedStatusChange.emit(status);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.statuses.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.dealStatusService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.statuses = this.statuses.concat(
                    data.items.filter(
                      (item) => !this.statuses.some((status) => status.id === item.id)
                    )
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 50);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.dealStatusService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedStatus?.id) && this._selectedStatus) {
            this.statuses = data.items.concat(this._selectedStatus);
          } else {
            this.statuses = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
